import React, { lazy, useEffect } from 'react';
import {
  Navigate, Route, Routes, useParams,
} from 'react-router-dom';
import ComandaContextOutlet from 'components/ComandaContextOutlet';
import { PrinterProvider } from 'contexts/printer';
import LoadingScreen from 'screens/Loading';
import NotFoundScreen from 'screens/NotFound';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import {
  activateAdminVenue,
  activeVenueCleared,
  selectActiveVenueId,
  selectAdminVenues,
  selectAdminVenuesStatus,
} from './redux/slices/admin.slice';

const ReadCodeScreen = lazy(() => import('screens/ReadCode'));
const BillLookupScreen = lazy(() => import('screens/BillLookup'));
const TablesScreen = lazy(() => import('screens/Tables'));
const ManageBillScreen = lazy(() => import('screens/ManageBill'));
const ManageBillPaymentScreen = lazy(() => import('screens/ManageBillPayment'));
const ManageCategoriesScreen = lazy(() => import('screens/ManageCategories'));
const ManageProductsScreen = lazy(() => import('screens/ManageProducts'));
const ManageSettingsScreen = lazy(() => import('screens/ManageSettings'));
const ManagePaymentMethodsScreen = lazy(() => import('screens/ManagePaymentMethods'));
const KitchenDashboardScreen = lazy(() => import('screens/KitchenDashboard'));
const ManagerReportItems = lazy(() => import('screens/ManagerReportItems'));
const ManagerReportsBills = lazy(() => import('screens/ManagerReportsBills'));
const ManagerReportsScreen = lazy(() => import('screens/ManagerReports'));
const VenuesScreen = lazy(() => import('screens/Venues'));

interface ManagerParams {
  venueId: string;
  [key: string]: string;
}

function VenueRoutes() {
  const dispatch = useAppDispatch();
  const params = useParams<ManagerParams>();
  const activeVenueId = useAppSelector(selectActiveVenueId);
  const venues = useAppSelector(selectAdminVenues);
  const venuesStatus = useAppSelector(selectAdminVenuesStatus);
  const canManageVenue = venues.some((venue) => venue.venueId === params.venueId);

  useEffect(() => {
    if (venuesStatus !== 'succeeded') return;

    if (params.venueId && canManageVenue) {
      dispatch(activateAdminVenue(params.venueId));
    } else {
      dispatch(activeVenueCleared());
    }
  }, [canManageVenue, dispatch, params.venueId, venuesStatus]);

  if (venuesStatus === 'idle' || venuesStatus === 'pending') {
    return <LoadingScreen description="Verificando acesso ao estabelecimento" />;
  }

  if (venuesStatus === 'failed') {
    return (
      <NotFoundScreen
        title="Não foi possível verificar seu acesso"
        description="Atualize a página e tente novamente."
      />
    );
  }

  if (!canManageVenue) return <Navigate to="/manager" replace />;
  if (activeVenueId !== params.venueId) {
    return <LoadingScreen description="Carregando estabelecimento" />;
  }

  return (
    <PrinterProvider key={params.venueId} values={{ usingVenue: params.venueId }}>
      <Routes>
        <Route
          path="read"
          element={(
            <ReadCodeScreen type="manager" headerStyles={{ backgroundColor: 'black' }} backLink={`/manager/${params.venueId}`} />
        )}
        />
        <Route path="p/:billId" element={<ComandaContextOutlet type="p" />}>
          <Route index element={<ManageBillScreen />} />
          <Route path="payments" element={<ManageBillPaymentScreen />} />
        </Route>
        <Route path="h/:billId" element={<ComandaContextOutlet type="h" />}>
          <Route index element={<BillLookupScreen type="h" />} />
        </Route>
        <Route path="reports">
          <Route path="soldItems" element={<ManagerReportItems />} />
          <Route path="bills" element={<ManagerReportsBills />} />
          <Route index element={<ManagerReportsScreen />} />
        </Route>
        <Route path="categories" element={<ManageCategoriesScreen />} />
        <Route path="monitor/kitchen" element={<KitchenDashboardScreen />} />
        <Route path="monitor/service" element={<KitchenDashboardScreen waiterView />} />
        <Route path="products" element={<ManageProductsScreen />} />
        <Route path="paymentMethods" element={<ManagePaymentMethodsScreen />} />
        <Route path="settings">
          <Route index element={<Navigate to="venue" replace />} />
          <Route path=":section" element={<ManageSettingsScreen />} />
        </Route>
        <Route index element={<TablesScreen />} />
      </Routes>
    </PrinterProvider>
  );
}

function Manager() {
  return (
    <Routes>
      <Route index element={<VenuesScreen />} />
      <Route path=":venueId/*" element={<VenueRoutes />} />
    </Routes>
  );
}

export default Manager;
